import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Typography,
    Chip,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Popover,
    Divider,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { AppButton } from '../../../shared/components/AppButton';
import { Loader } from '../../../shared/components/Loader';
import { getGoFrugalLogs, deleteGoFrugalLog } from '../../../shared/services/gofrugalLogService';
import { GoFrugalErrorLog, GoFrugalLogSource } from '../../../shared/types/gofrugalLog';
import moment from 'moment';

const SOURCE_LABELS: Record<GoFrugalLogSource, string> = {
    ORDER_SYNC: 'Order Sync',
    USER_SYNC: 'User Sync',
    PRODUCT_PULL_SYNC: 'Product Pull',
    USER_PULL_SYNC: 'User Pull',
    CATEGORY_PULL_SYNC: 'Category Pull',
    CATEGORY_VALUE_PULL_SYNC: 'Category Value Pull',
    STOCK_PULL_SYNC: 'Stock Pull',
    TAX_PULL_SYNC: 'Tax Pull',
    TRUE_ORDER_SESSION: 'Login/Session',
};

export const GoFrugalErrorLogs: React.FC = () => {
    const [logs, setLogs] = useState<GoFrugalErrorLog[]>([]);
    const [totalLogs, setTotalLogs] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [source, setSource] = useState<GoFrugalLogSource | ''>('');

    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const loadLogs = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const params: any = {
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput,
            };
            if (source) params.source = source;

            const response = await getGoFrugalLogs(params);

            if (response.success && response.data) {
                setLogs(response.data.items || []);
                setTotalLogs(response.data.pagination?.totalItems || (response.data.items || []).length);
            } else {
                setLogs([]);
                setTotalLogs(0);
            }
        } catch (err: any) {
            console.error('Error loading GoFrugal error logs:', err);
            setError(err.response?.data?.message || 'Failed to load GoFrugal error logs');
            setLogs([]);
            setTotalLogs(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, source, searchInput]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handleSourceChange = (event: SelectChangeEvent) => {
        setSource(event.target.value as GoFrugalLogSource | '');
        setCurrentPage(0);
    };

    const resetFilters = () => {
        setSearchInput('');
        setSource('');
        setCurrentPage(0);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(event.currentTarget);
        setSelectedLogId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedLogId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedLogId) return;
        try {
            setDeleting(true);
            await deleteGoFrugalLog(selectedLogId);
            await loadLogs();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete log');
        } finally {
            setDeleting(false);
            handleDeleteClose();
        }
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        GoFrugal Error Logs
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <FormControl variant="outlined" size="small" className="min-w-[180px]">
                        <InputLabel>Source</InputLabel>
                        <Select
                            value={source}
                            onChange={handleSourceChange}
                            label="Source"
                            disabled={loading}
                        >
                            <MenuItem value="">All Sources</MenuItem>
                            {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search by message or reference"
                        className="w-full sm:w-64"
                        disabled={loading}
                    />

                    <Box display="flex" gap={2}>
                        <AppButton
                            variant="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={loadLogs}
                            disabled={loading}
                        >
                            Refresh
                        </AppButton>
                        {(source || searchInput) && (
                            <AppButton
                                variant="secondary"
                                onClick={resetFilters}
                                disabled={loading}
                            >
                                Clear
                            </AppButton>
                        )}
                    </Box>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading && logs.length > 0 && <LinearProgress className="mb-4" />}

                    {loading && logs.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : logs.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {searchInput.trim() || source ? 'No logs match your filters.' : 'No GoFrugal errors logged. 🎉'}
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Source</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Operation</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Message</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Logged At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={logs.length === 0}
                                emptyColSpan={6}
                            >
                                {logs.map((log) => (
                                    <TableRow key={log.id} hover>
                                        <TableCell>
                                            <Chip
                                                label={SOURCE_LABELS[log.source] || log.source}
                                                size="small"
                                                className="font-bold"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {log.operation}
                                            </Typography>
                                            {log.referenceId && (
                                                <Typography variant="caption" color="textSecondary">
                                                    Ref: {log.referenceId}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {log.statusCode ?? '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={log.message}>
                                                <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 320 }}>
                                                    {log.message}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(log.createdAt).format('DD MMM YYYY, hh:mm A')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleDeleteClick(e, log.id)}
                                                    className="text-red-600 hover:bg-red-50"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={totalLogs}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1.5,
                        width: 260,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Are you sure?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This error log will be permanently deleted.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton variant="secondary" size="small" onClick={handleDeleteClose} disabled={deleting}>
                            Cancel
                        </AppButton>
                        <AppButton variant="danger" size="small" onClick={handleDeleteConfirm} loading={deleting}>
                            Delete
                        </AppButton>
                    </div>
                </div>
            </Popover>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setError('')} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </div>
    );
};
